import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import { Grid, Toolbar, Typography } from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomUpload from "../../../../components/CustomUpload";
import Select from "../../../../components/Select/Select";
import {
  deleteFileById,
  lockUnlockAppointmentById,
  updateFilesByIdWoLoader,
} from "../../../../redux/reducers/clinicSlice";
import {
  createApprovalRequest,
  getEmrSettings,
} from "../../../../redux/reducers/globalSettingsSlice";
import {
  getMedicalHistoryByPetId,
  getMedicalHistoryByPetIdWoLoader,
} from "../../../../redux/reducers/medicalHistorySlice";
import { getCompliantSummaryByIdWoLoader } from "../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../util/AppColors";
import { delBtn } from "../../../../util/CommonIconStyle";
import { a4PrintConfig } from "../../../../util/commonConfig";
import { serviceTypes, workbookStatusList } from "../../../../util/dropList";
import { genderEnum } from "../../../../util/enum";
import { printFromNode, warmPrintFrame } from "../../../../util/fastPrint";
import {
  getKey,
  getRightSaluation,
  urlToFile,
} from "../../../../util/function";
import { canWriteModule } from "../../../../util/roleModuleAccess";
import ContentToPrint from "../../../CommonScreens/ContentToPrint/ContentToPrint";
import BodyMapView from "../../../CommonScreens/WorkBook/BodyMapView";
import DiagnosticsView from "../../../CommonScreens/WorkBook/DiagnosticsView";
import FileView from "../../../CommonScreens/WorkBook/FileView";
import FormsView from "../../../CommonScreens/WorkBook/FormsView";
import PrescriptionView from "../../../CommonScreens/WorkBook/PrescriptionView";
import PreventiveView from "../../../CommonScreens/WorkBook/PreventiveView";
import ProceduresView from "../../../CommonScreens/WorkBook/ProceduresView";
import SoapNoteView from "../../../CommonScreens/WorkBook/SoapNoteView";
import TreatmentPlanView from "../../../CommonScreens/WorkBook/TreatmentPlanView";
import VitalsView from "../../../CommonScreens/WorkBook/VitalsView";
import FilesWithCloseButton from "../PatientWorkbook/FilesWithCloseButton";

const arrow = { width: 30, height: 30, color: AppColors.appPrimary };
const initSearch = {
  // startDate: null,
  // endDate: null,
  serviceTypes: [],
  vetNames: [],
  caseIds: [],
  reasons: [],
  // tags: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = ["Condition Status", "Condition", "Date"];
const dropFields = ["Condition Status", "Condition"];
const defObj = { clinic: {}, vets: [] };
const apntStatus = ["consultation", "recovery", "finalize", "completed"];
const editConsultFieldOptions = [
  { key: "admissionRemarks", label: "Admission Remarks" },
  { key: "redFlags", label: "Red Flags / Inpatient Alerts" },
  { key: "inpatientInstructions", label: "Inpatient Instructions" },
  { key: "soapNotes", label: "SOAP Notes" },
  { key: "procedures", label: "Procedures" },
  { key: "administered", label: "Administered" },
  { key: "notes", label: "Memo" },
  { key: "preventive", label: "Preventive" },
  { key: "diagnostics", label: "Diagnostics" },
  { key: "prescription", label: "Prescription" },
  { key: "consentForms", label: "Consent Form" },
  { key: "protocolForms", label: "Protocol Form" },
  { key: "ioMonitoring", label: "I/O Monitoring" },
  { key: "vitals", label: "Vitals" },
  { key: "bodyMap", label: "Body Map" },
  { key: "files", label: "Files" },
];

const getDischargeSummaryList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === "object") return [raw];
  return [];
};

const getLatestDischargeSummary = (raw) => {
  const list = getDischargeSummaryList(raw);
  if (!list.length) return null;
  return [...list].sort((a, b) => {
    const aId = Number(a?.dischargeSummaryId ?? a?.id ?? 0);
    const bId = Number(b?.dischargeSummaryId ?? b?.id ?? 0);
    if (aId !== bId) return bId - aId;
    const aTime = new Date(
      a?.updatedDate ?? a?.createdDate ?? a?.createdAt ?? 0,
    ).getTime();
    const bTime = new Date(
      b?.updatedDate ?? b?.createdDate ?? b?.createdAt ?? 0,
    ).getTime();
    return bTime - aTime;
  })[0];
};

const hasDischargeSummary = (raw) => getDischargeSummaryList(raw).length > 0;

const formatDischargeFollowUp = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(value);
};

const getDischargeInstructionRows = (summary) => {
  if (!summary || typeof summary !== "object") return [];

  const isDeceased = summary?.patientOutcome === "Deceased";
  const rows = [{ label: "Patient Outcome", value: summary?.patientOutcome }];

  if (isDeceased) {
    rows.push(
      {
        label: "Date of Death",
        value: formatDischargeFollowUp(summary?.dateOfDeath),
      },
      { label: "Time of Death", value: summary?.timeOfDeath },
      { label: "Circumstances", value: summary?.circumstances },
    );
  } else {
    rows.push(
      { label: "Diet Instructions", value: summary?.dietInstructions },
      {
        label: "Activity Restrictions",
        value: summary?.activityRestrictions,
      },
      {
        label: "Follow-up",
        value: [
          formatDischargeFollowUp(summary?.followUpDate),
          summary?.followUpTime,
        ]
          ?.filter((part) => String(part ?? "").trim().length > 0)
          ?.join(", "),
      },
      {
        label: "Owner Instructions / Warning Signs",
        value: summary?.ownerInstructions,
      },
    );
  }

  return rows.filter((row) => String(row?.value ?? "").trim().length > 0);
};

const DischargeInstructionsView = ({ dischargeSummary }) => {
  // dischargeSummary can be a single object or an array of summaries
  const summary = getLatestDischargeSummary(dischargeSummary);
  const rows = getDischargeInstructionRows(summary);
  if (!summary || rows.length === 0) return null;

  return (
    <>
      <div className="mt10 flex-row-ali-cen">
        <Typography className="font-bold fs14 blue-color">
          Discharge Instructions
        </Typography>
      </div>
      {rows.map((row) => (
        <div className="mt5 mh10" key={row.label}>
          <Typography className="fs12 txt-semi-bold black">
            {row.label}:
          </Typography>
          <Typography
            className="fs12 txt-regular black"
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
            component="div"
          >
            {String(row.value)
              ?.split("\n")
              ?.map((line, index) => (
                <div key={`${row.label}-${index}`}>{line || "\u00A0"}</div>
              ))}
          </Typography>
        </div>
      ))}
    </>
  );
};

const getInpatientAlertList = (raw) =>
  Array.isArray(raw) ? raw.filter((al) => String(al?.text ?? "").trim()) : [];

const getSortedInpatientAlerts = (raw) =>
  [...getInpatientAlertList(raw)].sort((a, b) => {
    const aTime = new Date(a?.updatedDate || a?.createdDate || 0).getTime();
    const bTime = new Date(b?.updatedDate || b?.createdDate || 0).getTime();
    if (bTime !== aTime) return bTime - aTime;
    return Number(b?.alertId ?? 0) - Number(a?.alertId ?? 0);
  });

const getInpatientSummaryList = (raw) =>
  Array.isArray(raw)
    ? raw.filter((sm) => String(sm?.instructions ?? "").trim())
    : [];

const getSortedInpatientSummaries = (raw) =>
  [...getInpatientSummaryList(raw)].sort((a, b) => {
    const aTime = new Date(a?.createdDate || 0).getTime();
    const bTime = new Date(b?.createdDate || 0).getTime();
    if (bTime !== aTime) return bTime - aTime;
    return Number(b?.summaryId ?? 0) - Number(a?.summaryId ?? 0);
  });

const AdmissionRemarkView = ({ remarks }) => {
  const text = String(remarks ?? "").trim();
  if (!text) return null;

  return (
    <>
      <div className="mt10 flex-row-ali-cen">
        <Typography className="font-bold fs14 blue-color">
          Reason for Admission / Provisional Diagnosis
        </Typography>
      </div>
      <Typography
        className="fs12 txt-regular black mt5"
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
        component="div"
      >
        {text.split("\n").map((line, index) => (
          <div key={`admission-remark-${index}`}>{line || "\u00A0"}</div>
        ))}
      </Typography>
    </>
  );
};

const InpatientRedFlagsView = ({ inpatientAlerts }) => {
  const alerts = getSortedInpatientAlerts(inpatientAlerts);
  if (!alerts.length) return null;

  return (
    <>
      <div className="mt10 flex-row-ali-cen">
        <Typography className="font-bold fs14 blue-color">Red Flags</Typography>
      </div>
      <div
        className="mt5"
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          backgroundColor: "#fafafa",
          padding: "10px 12px",
        }}
      >
        {alerts.map((alrt) => {
          const created = moment(alrt?.updatedDate || alrt?.createdDate);
          const time = created.isValid()
            ? created.format("DD MMM YYYY, hh:mm a")
            : "";
          return (
            <div key={alrt?.alertId} style={{ marginBottom: 8 }}>
              <Typography
                className="fs12 txt-regular black"
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
                component="div"
              >
                {(alrt?.text ?? "")?.split("\n")?.map((line, index) => (
                  <div key={`${alrt?.alertId}-${index}`}>
                    {line || "\u00A0"}
                  </div>
                ))}
              </Typography>
              {time || alrt?.doctorName ? (
                <Typography className="fs10 font-medium gray4">
                  {[alrt?.doctorName, time].filter(Boolean).join(" · ")}
                </Typography>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

const InpatientInstructionsView = ({ inpatientSummary }) => {
  const summaries = getSortedInpatientSummaries(inpatientSummary);
  if (!summaries.length) return null;

  return (
    <>
      <div className="mt10 flex-row-ali-cen">
        <Typography className="font-bold fs14 blue-color">
          Instructions
        </Typography>
      </div>
      <div
        className="mt5"
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          backgroundColor: "#fafafa",
          padding: "10px 12px",
        }}
      >
        {summaries.map((inp) => {
          const created = moment(inp?.createdDate);
          const time = created.isValid()
            ? created.format("DD MMM YYYY, hh:mm a")
            : "";
          return (
            <div key={inp?.summaryId} style={{ marginBottom: 8 }}>
              <Typography
                className="fs12 txt-regular black"
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
                component="div"
              >
                {(inp?.instructions ?? "")?.split("\n")?.map((line, index) => (
                  <div key={`${inp?.summaryId}-${index}`}>
                    {line || "\u00A0"}
                  </div>
                ))}
              </Typography>
              {time || inp?.doctorName ? (
                <Typography className="fs10 font-medium gray4">
                  {[inp?.doctorName, time].filter(Boolean).join(" · ")}
                </Typography>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

const getIoMonitoringList = (raw) =>
  Array.isArray(raw) ? raw.filter(Boolean) : [];

const buildIoLogDescription = (form) => {
  const category = String(form?.category || "")
    .toLowerCase()
    .trim();

  if (category === "iv fluid") {
    if (!form?.fluidType && !form?.rate && !form?.volumeInfused) {
      return "— running at ml/hr, ml infused so far";
    }
    return `${form.fluidType || "—"} — running at ${form.rate || ""} ml/hr, ${form.volumeInfused || ""} ml infused since start`;
  }
  if (category === "oral water") {
    return `Offered ${form.offeredMl || "—"} ml, consumed ${form.consumedMl || "—"} ml`;
  }
  if (category === "food") {
    return `${form.dietFoodType || "Food"} — offered ${form.offeredG || "—"} g, consumed ${form.consumedG || "—"}`;
  }
  if (category === "blood / blood products") {
    return `${form.productType || "Blood product"} — ${form.volumeMl || "—"} ml at ${form.rate || "—"} ml/hr`;
  }
  if (category === "urine") {
    return `${form.volume ? `${form.volume} ml` : "—"}, ${form.colourCharacter || "—"}`;
  }
  if (category === "vomiting") {
    const episodes = form.episodes || "1";
    const epLabel = String(episodes) === "1" ? "episode" : "episodes";
    return `${episodes} ${epLabel}, ${form.character || form.characterValue || "—"}`;
  }
  if (category === "feces") {
    return `${form.fecalScore || "—"}, ${form.notes || ""}`.trim();
  }
  if (category === "blood loss / hemorrhage") {
    return `${form.estimatedVolume || "—"}, source: ${form.sourceSite || "—"}`;
  }
  if (category.startsWith("other (")) {
    return form.description || "—";
  }
  if (category === "other nursing note") {
    return form.description || "—";
  }
  if (category === "pressure area check") {
    return form.finding || "—";
  }
  if (
    category.includes("catheter") ||
    category.includes("wound") ||
    category.includes("incision")
  ) {
    return `${form.site || "—"} — ${form.finding || "—"}`;
  }
  return form.description || "—";
};

const IOMonitoringSummaryView = ({ ioMonitoring }) => {
  const entries = getIoMonitoringList(ioMonitoring)
    .map((item) => {
      const created = moment(
        item?.createdDate || item?.createdAt || item?.updatedDate,
      );
      return {
        ...item,
        id: item?.ioMonitoringId ?? item?.id,
        categoryLabel: item?.category || item?.type || "",
        description:
          (item?.description && String(item.description).trim()) ||
          buildIoLogDescription(item),
        time: created.isValid() ? created.format("DD MMM YYYY, hh:mm a") : "",
        sortTime: created.isValid() ? created.valueOf() : 0,
      };
    })
    .sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0));

  if (!entries.length) return null;

  return (
    <>
      <div className="mt10 flex-row-ali-cen">
        <Typography className="font-bold fs14 blue-color">
          Monitoring & I/O Summary
        </Typography>
      </div>
      <div
        className="mt5"
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          backgroundColor: "#fafafa",
          padding: "10px 12px",
        }}
      >
        {entries.map((item) => (
          <div key={item.id} style={{ marginBottom: 8 }}>
            <Typography className="fs12 txt-regular black">
              {item.categoryLabel ? (
                <span className="txt-semi-bold">{item.categoryLabel}: </span>
              ) : null}
              {item.description}
            </Typography>
            {item.time ? (
              <Typography className="fs10 font-medium gray4">
                {item.time}
              </Typography>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
};

const getDailyProgressNotes = (inpatientNotes, appointmentId) =>
  (inpatientNotes ?? [])
    ?.filter(
      (note) =>
        note?.appointmentId === appointmentId &&
        note?.type?.toLowerCase() === "inpatient" &&
        String(note?.summary ?? "").trim().length > 0,
    )
    ?.sort(
      (a, b) => new Date(b?.createdDate ?? 0) - new Date(a?.createdDate ?? 0),
    );

const DailyProgressNotesView = ({ notes, appointmentId }) => {
  const progressNotes = getDailyProgressNotes(notes, appointmentId);
  if (!progressNotes.length) return null;

  return (
    <>
      <div className="mt10 flex-row-ali-cen">
        <Typography className="font-bold fs14 blue-color">
          Daily Progress Notes
        </Typography>
      </div>
      <div className="mt5 mh10">
        {progressNotes.map((note, index) => {
          const created = moment(note?.createdDate);
          const dateLabel = created.isValid()
            ? created.format("DD MMM YYYY, HH:mm")
            : "";
          return (
            <div
              key={note?.noteId ?? note?.id ?? `${note?.createdDate}-${index}`}
              className="flex-row"
              style={{
                borderBottom: "1px solid #e8e8e8",
                padding: "10px 0",
                gap: 16,
                alignItems: "flex-start",
              }}
            >
              <Typography
                className="fs12 txt-regular"
                style={{ minWidth: 110, flexShrink: 0 }}
              >
                {dateLabel}
              </Typography>
              <Typography
                className="fs12 txt-regular black"
                style={{
                  flex: 1,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {note?.summary}
              </Typography>
            </div>
          );
        })}
      </div>
    </>
  );
};

const MedicalHistoryNew = ({ petId, apntId }) => {
  const dispatch = useDispatch();
  const canManageDashboard = canWriteModule("dashboard");
  const printRef = useRef(null);
  const listInnerRef = useRef();
  const hasReachedBottomRef = useRef(false);
  const hasCalledAllHistoryRef = useRef(false);
  const [printApntId, setPrintApntId] = useState(null);
  const [activeTab, setActiveTab] = useState("Problem");
  const [samData, setSamData] = useState([]);
  const [heights, setHeights] = useState([]);
  const [page, setPage] = useState(1);
  const [medicalHistoryList, setMedicalHistoryList] = useState([]);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const [caseStatusMod, setCaseStatusMod] = useState({
    visible: false,
    item: null,
    caseStatus: "",
  });
  const [editConsultMod, setEditConsultMod] = useState({
    visible: false,
    item: null,
  });
  const [editRequestFor, setEditRequestFor] = useState([]);
  const [editRequestOthers, setEditRequestOthers] = useState("");
  const [editRequestErr, setEditRequestErr] = useState("");
  const [memoEditMod, setMemoEditMod] = useState({
    visible: false,
    item: null,
  });
  const [memoEditText, setMemoEditText] = useState("");
  const [memoEditFiles, setMemoEditFiles] = useState([]);
  const [memoEditError, setMemoEditError] = useState("");
  const [memoDelMod, setMemoDelMod] = useState({
    visible: false,
    item: null,
  });
  const { petMedicalHistoryAll } = useSelector(
    (state) => state?.medicalHistory,
  );
  // const clinicVets = useSelector((state) => state?.vet?.vets?.vets);
  // const petDet = useSelector(
  //   (state) => state?.pet?.complaintSummary?.data?.pet,
  // );
  const lastSummary = useSelector((state) => state?.pet?.lastSummary?.data);
  const emrSettings = useSelector(
    (state) => state?.globalSettings?.emrSettings,
  );
  const isEmrEnabled = (key) => {
    if (!emrSettings || typeof emrSettings !== "object") return true;
    if (emrSettings[key] == null) return true;
    return !!emrSettings[key];
  };
  const itemsPerLoad = 10;
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  const petData = petAndApntData?.pet;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinic: clinicDet, vets: clinicVets } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);

  useEffect(() => {
    dispatch(getEmrSettings({ silent: true }));
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    searchValues?.startDate,
    searchValues?.endDate,
    searchValues?.serviceTypes,
    searchValues?.vetNames,
    searchValues?.caseIds,
    searchValues?.reasons,
    searchValues?.tags,
  ]);

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const data = {
      type: "all",
      appointmentStatus: apntStatus,
      itemSize: itemsPerLoad,
      page: currentPage,
    };
    if (reset) return data;
    if (searchValues?.serviceTypes?.length > 0) {
      data.serviceTypes = searchValues?.serviceTypes?.map((li) => li?.value);
    }
    if (searchValues?.vetNames?.length > 0) {
      data.vetNames = searchValues?.vetNames?.map((li) => li?.apiValue);
    }
    if (searchValues?.caseIds?.length > 0) {
      data.caseIds = searchValues?.caseIds?.map((li) => li?.value);
    }
    if (searchValues?.reasons?.length > 0) {
      data.reasons = searchValues?.reasons?.map((li) => li?.value);
    }

    if (
      filterValues?.selectedList?.includes("Condition") &&
      filterValues?.values?.["Condition"]
    ) {
      data.tags = (filterValues?.values?.["Condition"] ?? [])?.map(
        (li) => li?.value,
      );
    }
    if (
      filterValues?.selectedList?.includes("Condition Status") &&
      filterValues?.values?.["Condition Status"]
    ) {
      data.caseStatus = (filterValues?.values?.["Condition Status"] ?? [])?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Date")) {
      if (filterValues?.values?.["Start Date"]) {
        data.startDate = moment(
          new Date(filterValues?.values?.["Start Date"]),
        ).format("YYYY-MM-DD");
      }
      if (
        filterValues?.values?.["End Date"] ||
        filterValues?.values?.["Start Date"]
      ) {
        data.endDate = moment(
          new Date(
            filterValues?.values?.["End Date"] ??
              filterValues?.values?.["Start Date"],
          ),
        ).format("YYYY-MM-DD");
      }
    }
    return data;
  };

  const onApiCall = (currPage = 1, reset = false) => {
    dispatch(
      getMedicalHistoryByPetId({ petId, data: getFilterObj(currPage, reset) }),
    ).then((res) => {
      if (res?.payload) {
        const selectedServiceTypes = (searchValues?.serviceTypes ?? []).map(
          (li) => li?.value,
        );
        const isOnlyMemoAndFiles =
          selectedServiceTypes.length === 1 &&
          selectedServiceTypes[0] === "Memo and Files";

        const appointments = isOnlyMemoAndFiles
          ? []
          : [...(res?.payload?.data?.appointments ?? [])].sort(
              (a, b) =>
                new Date(b?.appointmentTime) - new Date(a?.appointmentTime),
            );

        const petFileEntries =
          currPage === 1 || reset
            ? [...(res?.payload?.data?.petFiles ?? [])].sort(
                (a, b) => new Date(b?.createdDate) - new Date(a?.createdDate),
              )
            : [];

        const pageItems = (
          isOnlyMemoAndFiles
            ? petFileEntries
            : [...appointments, ...petFileEntries]
        ).sort((a, b) => {
          const dateA = a?.appointmentId
            ? new Date(a?.appointmentTime)
            : new Date(a?.createdDate);
          const dateB = b?.appointmentId
            ? new Date(b?.appointmentTime)
            : new Date(b?.createdDate);
          return dateB - dateA;
        });

        setMedicalHistoryList([
          ...(currPage === 1 || reset ? [] : (medicalHistoryList ?? [])),
          ...pageItems,
        ]);

        if (petId && !hasCalledAllHistoryRef.current) {
          hasCalledAllHistoryRef.current = true;
          dispatch(
            getMedicalHistoryByPetIdWoLoader({
              petId,
              data: {
                type: "all",
                appointmentStatus: apntStatus,
                page: 1,
                itemSize: 50,
              },
            }),
          );
        }
      }
    });
  };

  const handleOpen = (name, i, value) => {
    const reqData = samData?.map((dta, ind) => {
      if (i === ind) {
        return { ...dta, [name]: { ...dta[name], open: value } };
      }
      return dta;
    });
    setSamData(reqData);
  };

  const onScroll = () => {
    if (listInnerRef?.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef?.current;
      const threshold = 10; // Allow 10px threshold for floating point precision
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      const isAtBottom = distanceFromBottom <= threshold;

      if (isAtBottom && !hasReachedBottomRef.current) {
        hasReachedBottomRef.current = true;
        onApiCall(page + 1);
        // This will be triggered after hitting the last element.
        // API call should be made here while implementing pagination.
      } else if (!isAtBottom && hasReachedBottomRef.current) {
        // Reset flag when scrolling back up
        hasReachedBottomRef.current = false;
      }
    }
  };

  const getVetList = () => {
    const historyVets = (petMedicalHistoryAll?.data?.appointments ?? [])?.map(
      (li) => li?.doctorId,
    );
    const uniqVetList = uniq(historyVets ?? []);
    const reqVetList =
      (clinicVets ?? [])
        ?.filter(
          (vl) => vl?.status === "Y" && uniqVetList?.includes(vl?.doctorId),
        )
        ?.map((vet) => {
          return {
            name: `${getRightSaluation(clinicVets, vet?.doctorId)}${vet?.name}`,
            value: vet?.doctorId,
            id: vet?.doctorId,
            apiValue: vet?.name ?? "",
          };
        }) ?? [];
    return reqVetList;
  };

  const getCaseIds = () => {
    const reqList = (petMedicalHistoryAll?.data?.appointments ?? [])?.map(
      (li) => ({
        name: li?.caseId?.toString(),
        value: li?.caseId?.toString(),
      }),
    );
    return reqList;
  };

  const getSystemList = () => {
    const reqList = (petMedicalHistoryAll?.data?.appointments ?? [])
      ?.flatMap((app) => {
        const notes = app?.clinicalNotes || [];
        // If clinicalNotes exist → extract category from tag
        // if (notes?.length > 0) {
        //   return notes?.flatMap((note) => {
        //     const tag = note?.tag;
        //     if (!tag || !tag?.includes("-")) return [];
        //     return [tag?.substring(0, tag?.indexOf("-"))?.trim()];
        //   });
        // }
        // Else → fallback to reason array
        return app?.reason || [];
      })
      ?.filter(Boolean);
    return uniq(reqList ?? [])?.map((li) => ({ name: li, value: li }));
  };

  const getConditionList = () => {
    const reqList = (petMedicalHistoryAll?.data?.appointments ?? [])
      ?.flatMap((app) => app?.clinicalNotes || [])
      ?.flatMap((note) => {
        return note?.tag;
        // const tag = note?.tag;
        // if (!tag || !tag?.includes("-")) return [];
        // return [tag?.substring(tag?.indexOf("-") + 1)?.trim()];
      })
      ?.filter(Boolean);
    return uniq(reqList ?? [])?.map((li) => ({
      name: li?.substring(li?.indexOf("-") + 1)?.trim(),
      value: li,
    }));
  };

  const handleReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({
        ...initFilter,
        selectedList: [],
        values: {},
      });
    });
    onApiCall(1, true);
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const onFilterReset = () => {
    const reqObj = {
      ...initFilter,
      visible: true,
      selectedList: [],
      values: {},
    };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const onApplyFilter = () => {
    onApiCall();
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const handlePrintSummary = (apntId = null) => {
    if (!apntId) return;
    setPrintApntId(apntId);
    dispatch(getCompliantSummaryByIdWoLoader(apntId)).then((res) => {
      if (!res?.payload) setPrintApntId(null);
    });
  };

  const handlePrint = () => {
    printFromNode(printRef?.current, {
      title: `${lastSummary?.appointment?.petName ?? ""}_${Date.now()}`,
      pageStyle: a4PrintConfig,
    });
  };

  useEffect(() => {
    if (!printApntId || typeof window === "undefined") return;
    if (
      String(lastSummary?.appointment?.appointmentId) !== String(printApntId)
    ) {
      return;
    }
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const node = printRef?.current;
      if (node) warmPrintFrame(node, a4PrintConfig);
    };
    const tryPrint = () => {
      if (cancelled) return;
      const node = printRef?.current;
      if (!node) return;
      const imgs = [...node.querySelectorAll("img")];
      if (imgs.some((img) => !img.complete)) return;
      handlePrint();
      setPrintApntId(null);
    };
    const frameId = requestAnimationFrame(() => {
      warm();
      tryPrint();
    });
    const node = printRef?.current;
    const imgs = node ? [...node.querySelectorAll("img")] : [];
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", tryPrint, { once: true });
        img.addEventListener("error", tryPrint, { once: true });
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      imgs.forEach((img) => {
        img.removeEventListener("load", tryPrint);
        img.removeEventListener("error", tryPrint);
      });
    };
  }, [
    printApntId,
    lastSummary?.appointment?.appointmentId,
    lastSummary?.appointment?.petName,
  ]);

  const editConsultSelectList = useMemo(() => {
    const rows = editConsultFieldOptions
      .filter((row) => isEmrEnabled(row.key))
      .map((row) => ({ name: row.label, value: row.label }));
    return [...rows, { name: "Others", value: "Others" }];
  }, [emrSettings]);

  const onCloseEditConsultMod = () => {
    setEditConsultMod({ visible: false, item: null });
    setEditRequestFor([]);
    setEditRequestOthers("");
    setEditRequestErr("");
  };

  const onEditRequestChange = (e) => {
    setEditRequestErr("");
    const next = Array.isArray(e?.target?.value) ? e.target.value : [];
    setEditRequestFor(next);
    if (!next.some((item) => item?.value === "Others")) {
      setEditRequestOthers("");
    }
  };

  const onCloseMemoEditMod = () => {
    setMemoEditMod({ visible: false, item: null });
    setMemoEditText("");
    setMemoEditFiles([]);
    setMemoEditError("");
  };

  const onOpenMemoEdit = (itm) => {
    if (!itm?.fileId) return;
    const memo = (
      typeof itm?.notes === "string"
        ? itm.notes
        : typeof itm?.note === "string"
          ? itm.note
          : ""
    ).trim();
    const fileUrls = Array.isArray(itm?.files) ? itm.files.filter(Boolean) : [];
    setMemoEditMod({ visible: true, item: itm });
    setMemoEditText(memo);
    setMemoEditFiles(fileUrls.map((url) => ({ file: null, previewUrl: url })));
    setMemoEditError("");
  };

  const handleRemoveMemoEditFile = (ind) => {
    setMemoEditFiles((prev) => prev.filter((_, i) => i !== ind));
  };

  const onUploadMemoEditFile = (e) => {
    const fileList = e?.target?.files;
    let fileLists = memoEditFiles?.length > 0 ? [...memoEditFiles] : [];
    const readFiles = Array.from(fileList || []).map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          fileLists = [...fileLists, { file, previewUrl: reader.result }];
          resolve();
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
    });
    Promise.all(readFiles)
      .then(() => {
        setMemoEditFiles(fileLists);
        setMemoEditError("");
      })
      .catch((error) => console.error("Error reading files: ", error));
  };

  const handleMemoEditSubmit = async () => {
    const fileId = memoEditMod?.item?.fileId;
    if (!fileId) return;

    const hasMemo = !!memoEditText?.trim();
    const hasFiles = memoEditFiles?.length > 0;
    if (!hasMemo && !hasFiles) {
      setMemoEditError("Please enter a memo or upload at least 1 file");
      return;
    }

    const oldHadFiles =
      Array.isArray(memoEditMod?.item?.files) &&
      memoEditMod?.item?.files?.filter(Boolean)?.length > 0;

    const form = new FormData();
    form.append("name", "");
    form.append("petId", petId);
    if (hasMemo) form.append("note", memoEditText?.trim());

    if (hasFiles) {
      const filePromises = memoEditFiles?.map(async (fle) => {
        if (fle?.file) return fle.file;
        if (fle?.previewUrl) return await urlToFile(fle.previewUrl);
        return null;
      });
      const finalFiles = await Promise.all(filePromises);
      finalFiles?.forEach((file) => {
        if (file) form.append("files", file);
      });
    } else if (oldHadFiles) {
      form.append("deleteFile", true);
    }

    dispatch(updateFilesByIdWoLoader({ fileId, form })).then((res) => {
      if (res?.payload) {
        onCloseMemoEditMod();
        onApiCall(1, true);
      }
    });
  };

  const handleDeleteMemo = (itm) => {
    if (!itm?.fileId) return;
    setMemoDelMod({ visible: true, item: itm });
  };

  const onCloseMemoDelMod = () => {
    setMemoDelMod({ visible: false, item: null });
  };

  const onConfirmDeleteMemo = () => {
    const fileId = memoDelMod?.item?.fileId;
    if (!fileId) return;
    dispatch(
      deleteFileById({
        id: fileId,
        message: "Memo/Records removed successfully",
      }),
    ).then((res) => {
      if (res?.payload) {
        onCloseMemoDelMod();
        onApiCall(1, true);
      }
    });
  };

  const onConfirmEditConsult = async () => {
    const itm = editConsultMod?.item;
    if (!itm?.appointmentId) return;

    const appointmentId = itm?.appointmentId ?? null;
    const doctorId = itm?.doctorId ?? null;
    const userId = petData?.userId ?? null;
    const petName = petData?.petName || itm?.petName || "";
    const appointmentMoment = itm?.appointmentTime
      ? moment(new Date(itm.appointmentTime))
      : null;
    const hasAppointment = appointmentMoment?.isValid();
    const othersSelected = editRequestFor?.some(
      (item) => item?.value === "Others",
    );
    const othersRemarks = String(editRequestOthers ?? "").trim();
    const selectedEditRequestFor = editConsultFieldOptions
      ?.filter(
        (row) =>
          isEmrEnabled(row.key) &&
          editRequestFor?.some((item) => item?.value === row.label),
      )
      ?.map((row) => row.label);
    if (othersSelected && !othersRemarks) {
      setEditRequestErr("Enter remarks for Others");
      return;
    }
    if (othersSelected) {
      selectedEditRequestFor.push(`Others-${othersRemarks}`);
    }
    if (!selectedEditRequestFor?.length) {
      setEditRequestErr("Select at least one section");
      return;
    }
    const payload = {
      priority: "HIGH",
      requestType: "EDIT_CONSULT",
      summary: "Edit Consult",
      approvalStep: "",
      amount: 0,
      referenceType: "APPOINTMENT",
      referenceId: appointmentId,
      referenceNo: `Edit consultation requested for ${
        petName ? `${petName}'s ` : ""
      }appointment${
        hasAppointment
          ? ` on ${appointmentMoment.format("DD MMM YYYY")} at ${appointmentMoment.format("HH:mm")}`
          : ""
      }`,
      autoApproval: false,
      appointmentId,
      petName,
      petId,
      doctorId,
      userId,
      editRequestFor: selectedEditRequestFor?.join(", "),
      requestedBy: (() => {
        try {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          return user?.id || user?.doctorId || null;
        } catch {
          return null;
        }
      })(),
    };
    const approvalResult = await dispatch(createApprovalRequest(payload));
    if (!createApprovalRequest.fulfilled.match(approvalResult)) return;

    onCloseEditConsultMod();
  };

  const onLockUnlockApnt = (itm) => {
    if (!itm?.appointmentId) return;

    // Locked consult: confirm, then create approval request
    if (itm?.lock) {
      setEditRequestFor([]);
      setEditRequestOthers("");
      setEditRequestErr("");
      setEditConsultMod({ visible: true, item: itm });
      return;
    }

    dispatch(
      lockUnlockAppointmentById({
        id: itm?.appointmentId,
        url: `?lock=${!(itm?.lock ?? false)}`,
      }),
    ).then((res) => {
      if (res?.payload) {
        onApiCall(page);
      }
    });
  };

  const onOpenCaseStatusMod = (itm) => {
    if (!itm?.appointmentId) return;
    setCaseStatusMod({
      visible: true,
      item: itm,
      caseStatus: itm?.caseStatus || "",
    });
  };

  const onCloseCaseStatusMod = () => {
    setCaseStatusMod({
      visible: false,
      item: null,
      caseStatus: "",
    });
  };

  const onUpdateCaseStatus = () => {
    const itm = caseStatusMod?.item;
    const caseStatus = caseStatusMod?.caseStatus;
    if (!itm?.appointmentId || !caseStatus) return;

    dispatch(
      lockUnlockAppointmentById({
        id: itm.appointmentId,
        url: `?lock=${!(itm?.lock ?? false)}&caseStatus=${encodeURIComponent(caseStatus)}`,
      }),
    ).then((res) => {
      if (res?.payload) {
        onCloseCaseStatusMod();
        onApiCall(page);
      }
    });
  };

  return (
    <>
      <div
        className="scroll-80vh w100Per"
        onScroll={onScroll}
        ref={listInnerRef}
      >
        <Grid container className="back-white">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="ph20">
            <Toolbar variant="regular">
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="flex-row-ali-cen mt10 mh20Min flex-space-btwn">
                  <div className="w18Per">
                    <Select
                      list={[
                        ...serviceTypes,
                        { name: "Memo and Files", value: "Memo and Files" },
                      ]}
                      value={searchValues?.serviceTypes ?? []}
                      name="serviceTypes"
                      handleChange={onSearchChange}
                      label="Type"
                      backgroundColor={AppColors.lightBlue2}
                      multiSelectSearchCheck
                    />
                  </div>
                  <div className="w18Per">
                    <Select
                      list={getVetList()}
                      value={searchValues?.vetNames ?? []}
                      name="vetNames"
                      handleChange={onSearchChange}
                      label="Vet Name"
                      backgroundColor={AppColors.lightBlue2}
                      multiSelectSearchCheck
                    />
                  </div>
                  <div className="w18Per">
                    <Select
                      list={getCaseIds()}
                      value={searchValues?.caseIds ?? []}
                      name="caseIds"
                      handleChange={onSearchChange}
                      label="Case ID"
                      backgroundColor={AppColors.lightBlue2}
                      multiSelectSearchCheck
                    />
                  </div>
                  <div className="w18Per">
                    <Select
                      list={getSystemList()}
                      value={searchValues?.reasons ?? []}
                      name="reasons"
                      handleChange={onSearchChange}
                      label="System"
                      backgroundColor={AppColors.lightBlue2}
                      multiSelectSearchCheck
                    />
                  </div>
                  <div
                    className="flex-row-ali-cen filter cursor"
                    onClick={() => onFilterChange("visible", true)}
                  >
                    <TuneIcon sx={{ color: AppColors.appPrimary }} />
                    <Typography className="font-medium fs12 blue-color ml10">
                      {filterValues?.selectedList?.length > 0
                        ? `${filterValues?.selectedList?.length} `
                        : ""}
                      Filter
                    </Typography>
                  </div>
                  <div>
                    <div>
                      <CustomButton
                        text="Reset"
                        smallBtn
                        redBtn
                        onClick={handleReset}
                      />
                    </div>
                  </div>
                </div>
              </Grid>
            </Toolbar>
            {petData?.pastMedicalRecord?.length > 0 ? (
              <div className="flex-end">
                <a
                  href={petData?.pastMedicalRecord}
                  target="_blank"
                  className="red-color font-medium fs14 underline2 cursor"
                >
                  View Past Record
                </a>
              </div>
            ) : null}
            <div>
              {activeTab === "Problem" ? (
                // medicalHistory?.data
                medicalHistoryList?.length > 0 ? (
                  medicalHistoryList?.map((li, i) => {
                    const hasApnt = li?.appointmentId;
                    const reqDate = li?.createdDate;
                    const memoText = (
                      typeof li?.notes === "string"
                        ? li.notes
                        : typeof li?.note === "string"
                          ? li.note
                          : ""
                    ).trim();
                    const rawFiles = Array.isArray(li?.files)
                      ? li.files
                      : li?.files
                        ? [li.files]
                        : [];
                    const viewFiles =
                      rawFiles.length === 0
                        ? []
                        : typeof rawFiles[0] === "string"
                          ? [{ files: rawFiles }]
                          : Array.isArray(rawFiles[0]?.files) ||
                              rawFiles[0]?.fileId ||
                              rawFiles[0]?.name
                            ? rawFiles
                            : [{ files: rawFiles }];
                    const hasFiles = viewFiles.some((frm) =>
                      Array.isArray(frm?.files) ? frm.files.length > 0 : false,
                    );
                    const hasMemo = memoText.length > 0;

                    if (!hasApnt && !hasFiles && !hasMemo) return null;

                    return (
                      <div
                        className="w100Per getHeight"
                        key={getKey()}
                        // style={i !== 0 ? { marginTop: -40 } : { marginTop: 10 }}
                      >
                        <div className="flex-row mt20">
                          <div className="flex-column">
                            <div className="flex-center h50">
                              <div className="gry-dot" />
                            </div>
                            <div
                              style={{
                                width: 1,
                                height: heights?.[i] - 0,
                                background: "#00000033",
                                marginLeft: 5,
                                marginTop: -20,
                              }}
                            />
                          </div>

                          <div
                            className={`w10Per  ${li?.isMaster ? "" : "ml40"}`}
                          >
                            <div className="flex-column flex-center">
                              <Typography className="blue-color txt-semi-bold fs30 mb10Min">
                                {moment(hasApnt ? li?.date : reqDate).format(
                                  "DD",
                                )}
                              </Typography>
                              <Typography className="blue-color txt-semi-bold fs12">
                                {moment(hasApnt ? li?.date : reqDate).format(
                                  "MMM YYYY",
                                )}
                              </Typography>

                              <div
                                className={`mt5 ${
                                  li?.isMaster
                                    ? "org-time-highlight"
                                    : "rse-time-highlight"
                                }`}
                              />
                            </div>
                          </div>
                          <div className="w80Per">
                            {hasApnt ? (
                              <div className="his-bodr">
                                <div className="flex-row">
                                  <div className="flex-row-ali-cen p10">
                                    <div>
                                      <div className="flex-row-ali-cen">
                                        <Typography className="font-bold fs14 blue-color">
                                          Case ID - {li?.caseId}
                                        </Typography>
                                        <Typography
                                          className={`font-medium fs10 pv2 ph10 white-color br5 flex-center ml10 ${
                                            li?.appointmentType === "Physical"
                                              ? "card-bot-blue-back"
                                              : "virtual-bg"
                                          }`}
                                        >
                                          {li?.appointmentType === "Home"
                                            ? "Home Visit"
                                            : li?.appointmentType}
                                        </Typography>
                                        <Typography className="light-blue fs14 txt-semi-bold ml10">
                                          {li?.serviceType}
                                          {li?.serviceType === "Consultation"
                                            ? ` | ${
                                                Array.isArray(li?.reason)
                                                  ? li?.reason?.join(", ")
                                                  : ""
                                              }`
                                            : ""}
                                        </Typography>
                                        {/* {li?.tags?.map((tg, i) => (
                                      <Typography
                                        className="fs12 font-medium black19 ml10 gry-tag"
                                        key={tg + i}
                                      >
                                        {tg}
                                      </Typography>
                                    ))} */}
                                      </div>
                                      <div className="flex-row-ali-cen mt10">
                                        <img
                                          src={require("../../../../assets/images/png/calen.png")}
                                          className="img-wh15"
                                        />
                                        <Typography className="yellow2 fs12 txt-semi-bold ml5">
                                          {moment(
                                            new Date(li?.appointmentTime),
                                          ).format("DD MMM YYYY")}
                                        </Typography>
                                        <img
                                          src={require("../../../../assets/images/png/clock.png")}
                                          className="img-wh15 ml10"
                                        />
                                        <Typography className="yellow2 fs12 txt-semi-bold ml5">
                                          {moment(
                                            new Date(li?.appointmentTime),
                                          ).format("HH:mm")}
                                        </Typography>
                                        <img
                                          src={require("../../../../assets/images/png/steath.png")}
                                          className="img-wh15 ml10"
                                        />
                                        <Typography className="yellow2 fs12 txt-semi-bold ml5">
                                          {getRightSaluation(
                                            clinicVets,
                                            li?.doctorId,
                                          )}
                                          {/* {li?.doctorId === li?.clinicId
                                        ? ""
                                        : "Dr. "} */}
                                          {li?.doctorName}
                                        </Typography>

                                        {(
                                          Array.isArray(li?.kennelNumber)
                                            ? li?.kennelNumber?.length > 0
                                            : !!li?.kennelNumber
                                        ) ? (
                                          <Typography className="fs12 txt-semi-bold ml10">
                                            <span className="blue-color">
                                              Kennel:{" "}
                                            </span>
                                            <span className="black">
                                              {Array.isArray(li?.kennelNumber)
                                                ? li?.kennelNumber?.join(", ")
                                                : li?.kennelNumber}
                                            </span>
                                          </Typography>
                                        ) : null}
                                      </div>
                                    </div>
                                    <div className="flex1-end">
                                      <div className="flex-column">
                                        {/* <div className="flex-end">
                                    <Typography
                                      className={`fs12 font-bold white-color ${
                                        li?.status === "Under Treatment" ||
                                        li?.status === "Monitoring"
                                          ? "yellow-tag"
                                          : li?.status === "Inactive"
                                          ? "org-tag"
                                          : li?.status === "Resolved"
                                          ? "grn-tag"
                                          : "red-tag"
                                      }`}
                                    >
                                      {li?.status}
                                    </Typography>
                                  </div> */}
                                        {/* <div className="flex-row-ali-cen mt10">
                                    <Typography className="gray21 fs12 font-medium">
                                      Connections:
                                    </Typography>
                                    <Typography
                                      className={`fs12 font-medium ml5 ${
                                        li?.isMaster ? "rose" : "orange5"
                                      }`}
                                    >
                                      {li?.connections?.length}{" "}
                                      {li?.isMaster ? "Child" : "Master"}
                                    </Typography>
                                  </div> */}
                                      </div>
                                    </div>
                                  </div>
                                  {li?.caseStatus?.length > 0 ? (
                                    <div className="flex1-end p10">
                                      <Typography
                                        className={`fs12 white-color br10 ph10 pv2 ${(li?.caseStatus ?? "")?.toLowerCase() === "acute" ? "blue-back" : (li?.caseStatus ?? "")?.toLowerCase() === "ongoing" ? "org-back3" : (li?.caseStatus ?? "")?.toLowerCase() === "chronic" ? "purple-back" : "green-back6"}`}
                                      >
                                        {li?.caseStatus}
                                      </Typography>
                                    </div>
                                  ) : null}
                                </div>
                                <div className="ph5">
                                  <div className="gry-hor-split mt5" />
                                </div>
                                <div className="p10">
                                  <div key={getKey()}>
                                    {hasDischargeSummary(
                                      li?.dischargeSummary,
                                    ) &&
                                    getLatestDischargeSummary(
                                      li?.dischargeSummary,
                                    )?.finalDiagnosis?.trim?.()?.length > 0 ? (
                                      <>
                                        <div className="mt10 flex-row-ali-cen">
                                          <Typography className="font-bold fs14 blue-color">
                                            Final Diagnosis
                                          </Typography>
                                        </div>
                                        <Typography
                                          className="fs12 txt-regular black"
                                          style={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                          }}
                                          component="div"
                                        >
                                          {(
                                            getLatestDischargeSummary(
                                              li?.dischargeSummary,
                                            )?.finalDiagnosis ?? ""
                                          )
                                            ?.split("\n")
                                            ?.map((line, index) => (
                                              <div key={index}>
                                                {line || "\u00A0"}
                                              </div>
                                            ))}
                                        </Typography>
                                      </>
                                    ) : null}
                                    <AdmissionRemarkView
                                      remarks={li?.remarks}
                                    />
                                    <InpatientRedFlagsView
                                      inpatientAlerts={li?.inpatientAlert ?? []}
                                    />
                                    <InpatientInstructionsView
                                      inpatientSummary={li?.inpatientSummary}
                                    />
                                    {(li?.clinicalNotes?.find(
                                      (cn) =>
                                        cn?.appointmentId ===
                                          li?.appointmentId && cn?.consolidated,
                                    ) ??
                                    li?.clinicalNotes?.find(
                                      (cn) =>
                                        cn?.appointmentId ===
                                          li?.appointmentId &&
                                        !cn?.consolidated,
                                    )) ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "others",
                                              i,
                                              !li?.others?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.others?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Soap Notes
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="pr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>
                                        <SoapNoteView
                                          apntId={li?.appointmentId}
                                          soapNotes={li?.clinicalNotes?.filter(
                                            (cn) =>
                                              cn?.appointmentId ===
                                              li?.appointmentId,
                                          )}
                                          // soapNote={
                                          //   li?.clinicalNotes?.find(
                                          //     (cn) =>
                                          //       cn?.appointmentId ===
                                          //         li?.appointmentId &&
                                          //       cn?.consolidated,
                                          //   ) ??
                                          //   li?.clinicalNotes?.find(
                                          //     (cn) =>
                                          //       cn?.appointmentId ===
                                          //         li?.appointmentId &&
                                          //       !cn?.consolidated,
                                          //   )
                                          // }
                                        />
                                      </>
                                    ) : null}
                                    <DailyProgressNotesView
                                      notes={li?.inpatientNotes}
                                      appointmentId={li?.appointmentId}
                                    />
                                    {li?.procedures?.filter(
                                      (prd) =>
                                        prd?.appointmentId ===
                                        li?.appointmentId,
                                    )?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "procedures",
                                              i,
                                              !li?.procedures?.open,
                                            );
                                          }}
                                        >
                                          <Typography className="font-bold fs14 blue-color">
                                            Procedures
                                          </Typography>
                                        </div>
                                        <ProceduresView
                                          procedures={li?.procedures?.filter(
                                            (prd) =>
                                              prd?.appointmentId ===
                                              li?.appointmentId,
                                          )}
                                        />
                                      </>
                                    ) : null}
                                    {li?.procedures
                                      ?.filter(
                                        (prd) =>
                                          prd?.appointmentId ===
                                          li?.appointmentId,
                                      )
                                      ?.flatMap(
                                        (proc) => proc?.procedureDetails || [],
                                      )
                                      ?.filter(
                                        (det) =>
                                          det?.type === "Administered" &&
                                          det?.status === "true",
                                      )?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "administered",
                                              i,
                                              !li?.administered?.open,
                                            );
                                          }}
                                        >
                                          <Typography className="font-bold fs14 blue-color">
                                            Administered
                                          </Typography>
                                        </div>
                                        <ProceduresView
                                          procedures={li?.procedures?.filter(
                                            (prd) =>
                                              prd?.appointmentId ===
                                              li?.appointmentId,
                                          )}
                                          isAdminister
                                        />
                                      </>
                                    ) : null}
                                    {li?.notes?.length > 0 ? (
                                      <>
                                        <div
                                          className="flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "notes",
                                              i,
                                              !li?.notes?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.notes?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <div className="prescription-print-notes">
                                            <Typography
                                              className={`fs12 txt-semi-bold black`}
                                            >
                                              Notes
                                            </Typography>
                                            <Typography
                                              className="fs12 txt-regular black"
                                              style={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                              }}
                                              component="div"
                                            >
                                              {(li?.notes ?? "")
                                                ?.split("\n")
                                                ?.map((line, index) => (
                                                  <div key={index}>
                                                    {line || "\u00A0"}
                                                  </div>
                                                ))}
                                            </Typography>
                                          </div>
                                        </div>
                                        {/* <Typography className="fs12 txt-regular gray11">
                                        {li?.notes}
                                      </Typography> */}
                                      </>
                                    ) : null}
                                    {/* <Typography className="font-bold fs14 blue-color">
                                  Summary
                                </Typography>
                                <Typography className="txt-regular fs12 gray11 ph20">
                                  Test
                                </Typography> */}
                                    {li?.prevetive?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "preventives",
                                              i,
                                              !li?.preventives?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.preventives?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Preventive
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="mr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>

                                        <PreventiveView
                                          preventives={li?.prevetive}
                                        />
                                      </>
                                    ) : null}
                                    {li?.diagnostics?.filter(
                                      (diag) => diag?.dueDate === li?.date,
                                      // li?.status?.toLowerCase() === "completed"
                                    )?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "diagnostics",
                                              i,
                                              !li?.diagnostics?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.diagnostics?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Diagnostics
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="pr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>

                                        <DiagnosticsView
                                          diagnostics={li?.diagnostics?.filter(
                                            (dia) =>
                                              dia?.status?.toLowerCase() !==
                                              "scheduled",
                                          )}
                                          showFileAtBottom
                                          isFilePreview
                                          smallSize
                                        />
                                      </>
                                    ) : null}
                                    {li?.prescription?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "prescriptions",
                                              i,
                                              !li?.prescriptions?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.prescriptions?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Prescription
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="mr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>
                                        <PrescriptionView
                                          prescriptions={li?.prescription}
                                          showShortName={true}
                                        />
                                      </>
                                    ) : null}
                                    {(li?.inpatientNotes?.find(
                                      (cn) =>
                                        cn?.appointmentId ===
                                          li?.appointmentId &&
                                        cn?.type === "surgery" &&
                                        cn?.consolidated,
                                    ) ??
                                    li?.inpatientNotes?.find(
                                      (cn) =>
                                        cn?.appointmentId ===
                                          li?.appointmentId &&
                                        cn?.type === "surgery",
                                    )) ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "others",
                                              i,
                                              !li?.others?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.others?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Surgery Documentation
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="pr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>
                                        <SoapNoteView
                                          apntId={li?.appointmentId}
                                          soapNote={
                                            li?.inpatientNotes?.find(
                                              (cn) =>
                                                cn?.appointmentId ===
                                                  li?.appointmentId &&
                                                cn?.type === "surgery" &&
                                                cn?.consolidated,
                                            ) ??
                                            li?.inpatientNotes?.find(
                                              (cn) =>
                                                cn?.appointmentId ===
                                                  li?.appointmentId &&
                                                cn?.type === "surgery",
                                            )
                                          }
                                          isSurgery
                                        />
                                      </>
                                    ) : null}
                                    {li?.treatmentPlan?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "others",
                                              i,
                                              !li?.others?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.others?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Protocol Form
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="pr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>
                                        <TreatmentPlanView
                                          plans={li?.treatmentPlan}
                                        />
                                      </>
                                    ) : null}
                                    {li?.forms?.filter((frm) => frm?.status)
                                      ?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "others",
                                              i,
                                              !li?.others?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.others?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Consent Form
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="pr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>
                                        <FormsView
                                          forms={li?.forms?.filter(
                                            (frm) => frm?.status,
                                          )}
                                        />
                                      </>
                                    ) : null}
                                    {getIoMonitoringList(li?.ioMonitoring)
                                      .length > 0 ? (
                                      <IOMonitoringSummaryView
                                        ioMonitoring={li?.ioMonitoring}
                                      />
                                    ) : null}
                                    {li?.vitals?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "others",
                                              i,
                                              !li?.others?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.others?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Vitals
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="pr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>
                                        <VitalsView vitals={li?.vitals} />
                                      </>
                                    ) : null}
                                    {li?.bodyMaps?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "others",
                                              i,
                                              !li?.others?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.others?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Body Map
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="pr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>
                                        <BodyMapView bodyMaps={li?.bodyMaps} />
                                      </>
                                    ) : null}
                                    {li?.petFiles?.filter(
                                      (fl) => fl?.appointmentId,
                                    )?.length > 0 ? (
                                      <>
                                        <div
                                          className="mt10 flex-row-ali-cen"
                                          onClick={() => {
                                            handleOpen(
                                              "others",
                                              i,
                                              !li?.others?.open,
                                            );
                                          }}
                                        >
                                          {/* {li?.others?.open ? (
                                    <ArrowDropDownIcon sx={arrow} />
                                  ) : (
                                    <ArrowRightIcon sx={arrow} />
                                  )} */}
                                          <Typography className="font-bold fs14 blue-color">
                                            Files
                                          </Typography>
                                          {/* <div className="flex1-end">
                                    <div className="pr10">
                                      <CustomButton text="View" smallBtn />
                                    </div>
                                  </div> */}
                                        </div>
                                        <FileView
                                          files={li?.petFiles?.filter(
                                            (fl) => fl?.appointmentId,
                                          )}
                                        />
                                      </>
                                    ) : null}
                                    {hasDischargeSummary(
                                      li?.dischargeSummary,
                                    ) ? (
                                      <DischargeInstructionsView
                                        dischargeSummary={li?.dischargeSummary}
                                      />
                                    ) : null}
                                    <div className="flex1-end mt10">
                                      {/* {li?.appointmentId ? (
                                        <div className="mr10">
                                          <CustomButton
                                            text="Update Status"
                                            smallBtn
                                            onClick={() =>
                                              onOpenCaseStatusMod(li)
                                            }
                                          />
                                        </div>
                                      ) : null} */}
                                      {!apntId &&
                                      canManageDashboard &&
                                      (
                                        li?.appointmentStatus ?? ""
                                      )?.toLowerCase() === "completed" &&
                                      li?.lock ? (
                                        <div className="mr10">
                                          <CustomButton
                                            text={
                                              li?.lock
                                                ? "Edit Consult"
                                                : "Lock Consult"
                                            }
                                            smallBtn
                                            onClick={() => onLockUnlockApnt(li)}
                                            redBtn={!li?.lock}
                                            disabled={
                                              !clinicDet?.allowEditConsultations
                                            }
                                            grayBtn={
                                              !clinicDet?.allowEditConsultations
                                            }
                                          />
                                        </div>
                                      ) : null}
                                      <div>
                                        <CustomButton
                                          text="Print"
                                          smallBtn
                                          onClick={() =>
                                            handlePrintSummary(
                                              li?.appointmentId,
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                    {/* <div
                                  className="mt10 flex-row-ali-cen"
                                  onClick={() => {
                                    handleOpen("others", i, !li?.others?.open);
                                  }}
                                >
                                  <Typography className="font-bold fs14 blue-color">
                                    Others
                                  </Typography>
                                </div>

                                {li?.others?.open ? (
                                  li?.others?.list?.length > 0 ? (
                                    li?.others?.list?.map((pre, i) => (
                                      <div
                                        className="mt5 mh10"
                                        key={i + "other"}
                                      >
                                        <div className="gray-back-con1">
                                          <div className="flex-row-wrap1">
                                            <Typography className="fs12 txt-semi-bold black">
                                              Product Name: Anobivac
                                            </Typography>
                                            <div className="flex-row-ali-cen ml10">
                                              <div className="gray-dot" />
                                              <Typography className="font-medium fs12 black ml5">
                                                Vaccine:
                                              </Typography>
                                              <Typography className="txt-regular fs12 black ml5">
                                                Hepatitis
                                              </Typography>
                                            </div>
                                            <div className="flex-row-ali-cen ml10">
                                              <div className="gray-dot" />
                                              <Typography className="font-medium fs12 black ml5">
                                                Manufacturer:
                                              </Typography>
                                              <Typography className="txt-regular fs12 black ml5">
                                                Healthcare Plus
                                              </Typography>
                                            </div>
                                            <div className="flex-row-ali-cen ml10">
                                              <div className="gray-dot" />
                                              <Typography className="font-medium fs12 black ml5">
                                                Batch No:
                                              </Typography>
                                              <Typography className="txt-regular fs12 black ml5">
                                                B2
                                              </Typography>
                                            </div>
                                            <div className="flex-row-ali-cen ml10">
                                              <div className="gray-dot" />
                                              <Typography className="font-medium fs12 black ml5">
                                                Dose:
                                              </Typography>
                                              <Typography className="txt-regular fs12 black ml5">
                                                1 mg
                                              </Typography>
                                            </div>

                                            <div className="flex-row-ali-cen ml10">
                                              <div className="gray-dot" />
                                              <Typography className="font-medium fs12 black ml5">
                                                Administered date & by:
                                              </Typography>
                                              <Typography className="txt-regular fs12 black ml5">
                                                12 - 06 - 2024 | Dr Rahul
                                              </Typography>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="no-rec">
                                      No records available
                                    </div>
                                  )
                                ) : null}

                                <div className="blue-box-brdr mt10">
                                  <Typography className="font-bold fs14 blue-color">
                                    Additional Notes
                                  </Typography>
                                  <Typography className="txt-regular fs12 gray11 ph20">
                                    {li?.additionalNotes}
                                  </Typography>
                                </div> */}
                                  </div>
                                </div>
                              </div>
                            ) : hasFiles || hasMemo ? (
                              <div className="his-bodr">
                                <div className="flex-row">
                                  <div className="flex-row-ali-cen p10">
                                    <div>
                                      <div className="flex-row-ali-cen">
                                        <Typography className="font-bold fs14 blue-color">
                                          Memo/Records
                                        </Typography>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="ph5">
                                  <div className="gry-hor-split mt5" />
                                </div>
                                <div className="p10">
                                  {hasFiles ? (
                                    <>
                                      <div className="flex-row-ali-cen mt5">
                                        <Typography className="fs14 blue-color font-bold">
                                          Records
                                        </Typography>
                                      </div>
                                      <FileView files={viewFiles} />
                                    </>
                                  ) : null}
                                  {hasMemo ? (
                                    <div className="prescription-print-notes mt10">
                                      <Typography className="fs12 txt-semi-bold black">
                                        Memo
                                      </Typography>
                                      <Typography
                                        key={`memo_${i}`}
                                        className="fs12 txt-regular black mt5"
                                        style={{
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                        }}
                                        component="div"
                                      >
                                        {memoText
                                          .split("\n")
                                          .map((line, index) => (
                                            <div key={index}>
                                              {line || "\u00A0"}
                                            </div>
                                          ))}
                                      </Typography>
                                    </div>
                                  ) : null}
                                  {li?.fileId ? (
                                    <div className="flex1-end flex-row-ali-cen mt10">
                                      <div
                                        className="cursor mr15"
                                        onClick={() => handleDeleteMemo(li)}
                                      >
                                        <div className="flex-center">
                                          <DeleteOutlineIcon sx={delBtn} />
                                        </div>
                                        <Typography className="red3 fs8 txt-semi-bold text-align-center">
                                          Delete
                                        </Typography>
                                      </div>
                                      <div>
                                        <CustomButton
                                          text="Edit"
                                          smallBtn
                                          onClick={() => onOpenMemoEdit(li)}
                                        />
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-rec h100">No records available</div>
                )
              ) : null}
            </div>
          </Grid>
        </Grid>
      </div>

      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <div>
                      <CustomCheckbox
                        checked={filterValues?.selectedList?.includes(li)}
                        onChange={() => onSelectFilter(li)}
                      />
                    </div>
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  <div>
                    {filterValues?.selectedList?.map((li) => (
                      <div className="mt20">
                        {dropFields?.includes(li) ? (
                          <Select
                            list={
                              li === "Condition"
                                ? getConditionList()
                                : li === "Condition Status"
                                  ? workbookStatusList
                                  : []
                            }
                            value={filterValues?.values?.[li] ?? []}
                            name={li}
                            handleChange={onFilterValuesChange}
                            label={li}
                            multiSelectSearchCheck
                          />
                        ) : (
                          <div className="flex-row-between-align-center">
                            <div className="w45Per">
                              <CustomTextField
                                placeholder="Start Date"
                                fullWidth
                                name="Start Date"
                                handleChange={onFilterValuesChange}
                                value={filterValues?.values?.["Start Date"]}
                                label="Start Date"
                                mobileDate
                              />
                            </div>
                            <div className="w45Per">
                              <CustomTextField
                                placeholder="End Date"
                                fullWidth
                                name="End Date"
                                handleChange={onFilterValuesChange}
                                value={filterValues?.values?.["End Date"]}
                                label="End Date"
                                mobileDate
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <div>
                    <CustomButton
                      text="Add"
                      smallBtn
                      onClick={() => onFilterAdd(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <div>
                    <CustomButton
                      text="Reset All"
                      smallBtn
                      onClick={onFilterReset}
                      tabSelectdBtn
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>

      <div style={{ display: "none" }}>
        <ContentToPrint
          ref={printRef}
          rootClassName="workbook-history-print"
          footer={
            clinicDet?.footer?.length > 0 ? (
              <img src={clinicDet?.footer} alt="" />
            ) : null
          }
          header={
            <div className="flex-row-ali-cen mv20">
              <div className="w35Per">
                <div>
                  {clinicDet?.image ? (
                    <img src={clinicDet?.image} className="img-h75" alt="" />
                  ) : (
                    <div className="flex-center h50img">
                      <Typography className="font-bold fs30 white-color capitalize">
                        {clinicDet?.name?.[0]}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
              <div className="w30Per flex-center">
                <div className="font-bold fs20 blue-color">Medical Record</div>
              </div>
              <div className="w35Per flex1-end">
                <div className="prescription-doc-header-clinic">
                  {/* <div className="font-bold fs12 blue-color">
                      Business Name:
                    </div> */}
                  <div className="font-bold blue-color fs16">
                    {clinicDet?.name ?? ""}
                  </div>
                  {/* <div className="font-bold fs12 blue-color mt5">
                      Business Address:
                    </div> */}
                  <div className="txt-regular fs12 blue-color">
                    {clinicDet?.address ?? ""}
                  </div>
                  {clinicDet?.taxId?.length > 0 ? (
                    <div className="flex-row-ali-cen mt5">
                      <div className="font-bold fs12 blue-color">
                        {/* CLINIC  */}
                        TAX ID:{" "}
                      </div>
                      <div className="txt-regular fs12 blue-color ml3">
                        {clinicDet?.taxId ?? ""}
                      </div>
                    </div>
                  ) : null}
                  {clinicDet?.mobile?.length > 0 ? (
                    <div className="flex-row-ali-cen mt5">
                      <div className="font-bold fs12 blue-color">Contact: </div>
                      <div className="txt-regular fs12 blue-color ml3">
                        {clinicDet?.mobile ?? ""}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          }
        >
          <tr>
            <td className="prescription-print-tbody-cell">
              <div className="prescription-print-body-inner">
                <div className="p5">
                  <div className="mh20 mb20">
                    <div className="flex-row-ali-cen">
                      <div className="w100Per">
                        <div className="flex-row-ali-cen">
                          <Typography className="fs14 blue-color font-bold">
                            Case ID - {lastSummary?.appointment?.caseId}
                          </Typography>
                          <Typography
                            className={`font-medium fs10 pv2 ph10 white-color br5 flex-center ml10 ${
                              lastSummary?.appointment?.appointmentType ===
                              "Physical"
                                ? "card-bot-blue-back"
                                : "virtual-bg"
                            }`}
                          >
                            {lastSummary?.appointment?.appointmentType ===
                            "Home"
                              ? "Home Visit"
                              : lastSummary?.appointment?.appointmentType}
                          </Typography>
                          <Typography className="light-blue fs14 txt-semi-bold ml10">
                            {lastSummary?.appointment?.serviceType}
                            {lastSummary?.appointment?.serviceType ===
                              "Consultation" &&
                            lastSummary?.appointment?.reason?.length > 0
                              ? ` | ${
                                  Array.isArray(
                                    lastSummary?.appointment?.reason,
                                  )
                                    ? lastSummary?.appointment?.reason?.join(
                                        ", ",
                                      )
                                    : (lastSummary?.appointment?.reason ?? "")
                                }`
                              : ""}
                          </Typography>
                        </div>
                        <div className="flex-row-ali-cen mt10">
                          {lastSummary?.appointment?.userName?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Customer Name:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {lastSummary?.appointment?.userName}
                              </Typography>
                            </div>
                          ) : null}
                          {lastSummary?.appointment?.petName?.length > 0 ? (
                            <div className="flex-row-ali-cen ml10">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Pet Name:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {lastSummary?.appointment?.petName}
                              </Typography>
                            </div>
                          ) : null}
                          {lastSummary?.appointment?.petType?.length > 0 ? (
                            <div className="flex-row-ali-cen ml10">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Species:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {lastSummary?.appointment?.petType}
                              </Typography>
                            </div>
                          ) : null}
                        </div>
                        <div className="flex-row-ali-cen mt10">
                          {lastSummary?.appointment?.breed?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Breed:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {lastSummary?.appointment?.breed}
                              </Typography>
                            </div>
                          ) : null}
                          {genderEnum?.[lastSummary?.appointment?.gender]
                            ?.length > 0 ? (
                            <div className="flex-row-ali-cen ml10">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Gender:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {genderEnum?.[lastSummary?.appointment?.gender]}
                              </Typography>
                            </div>
                          ) : null}
                          {lastSummary?.appointment?.weight?.length > 0 ? (
                            <div className="flex-row-ali-cen ml10">
                              <Typography className="blue-color fs14 txt-semi-bold">
                                Weight:
                              </Typography>
                              <Typography className="black fs14 font-medium ml5">
                                {lastSummary?.appointment?.weight}
                              </Typography>
                            </div>
                          ) : null}
                        </div>
                        <div className="flex-row-ali-cen mt10">
                          <div className="flex-row-ali-cen">
                            <img
                              src={require("../../../../assets/images/png/calen.png")}
                              className="img-wh15"
                            />
                            <Typography className="yellow2 fs14 txt-semi-bold ml5">
                              {moment(
                                new Date(
                                  lastSummary?.appointment?.appointmentDate,
                                ),
                              ).format("DD MMM YYYY")}
                            </Typography>
                          </div>
                          <div className="flex-row-ali-cen ml10">
                            <img
                              src={require("../../../../assets/images/png/clock.png")}
                              className="img-wh15"
                            />
                            <Typography className="yellow2 fs14 txt-semi-bold ml5">
                              {lastSummary?.appointment?.appointmentStartTime ??
                                moment(
                                  new Date(
                                    lastSummary?.appointment
                                      ?.appointmentdateTime,
                                  ),
                                ).format("HH:mm")}
                            </Typography>
                          </div>
                          <div className="flex-row-ali-cen ml10">
                            <img
                              src={require("../../../../assets/images/png/steath.png")}
                              className="img-wh20"
                            />
                            <Typography className="yellow2 fs14 txt-semi-bold ml5">
                              {getRightSaluation(
                                clinicVets,
                                lastSummary?.appointment?.doctorId,
                              )}
                              {lastSummary?.appointment?.doctorName}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="gry-hor-split mv15" />
                    {hasDischargeSummary(lastSummary?.dischargeSummary) &&
                    getLatestDischargeSummary(
                      lastSummary?.dischargeSummary,
                    )?.finalDiagnosis?.trim?.()?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Final Diagnosis
                        </Typography>
                        <Typography
                          className="fs12 txt-regular black"
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                          component="div"
                        >
                          {(
                            getLatestDischargeSummary(
                              lastSummary?.dischargeSummary,
                            )?.finalDiagnosis ?? ""
                          )
                            ?.split("\n")
                            ?.map((line, index) => (
                              <div key={index}>{line || "\u00A0"}</div>
                            ))}
                        </Typography>
                      </>
                    ) : null}
                    {isEmrEnabled("admissionRemarks") ? (
                      <AdmissionRemarkView remarks={lastSummary?.remarks} />
                    ) : null}
                    {isEmrEnabled("redFlags") ? (
                      <InpatientRedFlagsView
                        inpatientAlerts={lastSummary?.inpatientAlert ?? []}
                      />
                    ) : null}
                    {isEmrEnabled("inpatientInstructions") ? (
                      <InpatientInstructionsView
                        inpatientSummary={lastSummary?.inpatientSummary}
                      />
                    ) : null}
                    {/* {lastSummary?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                          lastSummary?.appointment?.appointmentId,
                      )?.conclusion?.length > 0 ? (
                        <>
                          <div className="flex-row-ali-cen mt10">
                            <Typography className="fs14 blue-color font-bold">
                              Overview
                            </Typography>
                          </div>

                          <Typography className="fs12 txt-regular black">
                            {
                              lastSummary?.clinicalNotes?.find(
                                (cn) =>
                                  cn?.appointmentId ===
                                  lastSummary?.appointment?.appointmentId,
                              )?.conclusion
                            }
                          </Typography>
                        </>
                      ) : null} */}
                    {isEmrEnabled("soapNotes") &&
                    (lastSummary?.clinicalNotes?.find(
                      (cn) =>
                        cn?.appointmentId ===
                          lastSummary?.appointment?.appointmentId &&
                        cn?.consolidated,
                    ) ??
                      lastSummary?.clinicalNotes?.find(
                        (cn) =>
                          cn?.appointmentId ===
                            lastSummary?.appointment?.appointmentId &&
                          !cn?.consolidated,
                      )) ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Soap Notes
                        </Typography>
                        <SoapNoteView
                          apntId={lastSummary?.appointment?.appointmentId}
                          soapNotes={lastSummary?.clinicalNotes?.filter(
                            (cn) =>
                              cn?.appointmentId ===
                              lastSummary?.appointment?.appointmentId,
                          )}
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("procedures") &&
                    lastSummary?.procedures?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Procedures
                        </Typography>
                        <div className="mt10" />
                        <ProceduresView procedures={lastSummary?.procedures} />
                      </>
                    ) : null}
                    {isEmrEnabled("administered") &&
                    lastSummary?.procedures?.length > 0 &&
                    lastSummary?.procedures
                      ?.flatMap((proc) => proc?.procedureDetails || [])
                      ?.filter(
                        (det) =>
                          det?.type === "Administered" &&
                          det?.status === "true",
                      )?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Administered
                        </Typography>
                        <div className="mt10" />
                        <ProceduresView
                          procedures={lastSummary?.procedures}
                          isAdminister
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("notes") &&
                    lastSummary?.appointment?.notes?.length > 0 ? (
                      <>
                        <div className="prescription-print-notes workbook-print-section">
                          <Typography className={`fs12 txt-semi-bold black`}>
                            Memo
                          </Typography>
                          <Typography
                            className="fs12 txt-regular black"
                            style={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                            component="div"
                          >
                            {(lastSummary?.appointment?.notes ?? "")
                              ?.split("\n")
                              ?.map((line, index) => (
                                <div key={index}>{line || "\u00A0"}</div>
                              ))}
                          </Typography>
                        </div>
                      </>
                    ) : null}
                    {isEmrEnabled("preventive") &&
                    lastSummary?.prevetive?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Preventive
                        </Typography>
                        <PreventiveView preventives={lastSummary?.prevetive} />
                      </>
                    ) : null}
                    {isEmrEnabled("diagnostics") &&
                    lastSummary?.diagnostics?.filter(
                      (li) => li?.status?.toLowerCase() !== "scheduled",
                    )?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Diagnostics
                        </Typography>
                        <div className="mt10" />
                        <DiagnosticsView
                          diagnostics={lastSummary?.diagnostics?.filter(
                            (dia) => dia?.status?.toLowerCase() !== "scheduled",
                          )}
                          showFileAtBottom
                          isFilePreview
                          smallSize
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("prescription") &&
                    (lastSummary?.prescription ?? [])?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Prescription
                        </Typography>
                        <div className="mt10" />
                        <PrescriptionView
                          prescriptions={lastSummary?.prescription ?? []}
                          showShortName={true}
                        />
                      </>
                    ) : null}
                    {(lastSummary?.inpatientNotes?.find(
                      (cn) =>
                        cn?.appointmentId ===
                          lastSummary?.appointment?.appointmentId &&
                        cn?.type === "surgery" &&
                        cn?.consolidated,
                    ) ??
                    lastSummary?.inpatientNotes?.find(
                      (cn) =>
                        cn?.appointmentId ===
                          lastSummary?.appointment?.appointmentId &&
                        cn?.type === "surgery",
                    )) ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Surgery Documentation
                        </Typography>
                        <SoapNoteView
                          apntId={lastSummary?.appointment?.appointmentId}
                          soapNote={
                            lastSummary?.inpatientNotes?.find(
                              (cn) =>
                                cn?.appointmentId ===
                                  lastSummary?.appointment?.appointmentId &&
                                cn?.type === "surgery" &&
                                cn?.consolidated,
                            ) ??
                            lastSummary?.inpatientNotes?.find(
                              (cn) =>
                                cn?.appointmentId ===
                                  lastSummary?.appointment?.appointmentId &&
                                cn?.type === "surgery",
                            )
                          }
                          isSurgery
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("consentForms") &&
                    lastSummary?.forms?.filter((li) => li?.status)?.length >
                      0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Consent Form
                        </Typography>
                        <FormsView
                          forms={lastSummary?.forms?.filter((li) => li?.status)}
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("protocolForms") &&
                    (lastSummary?.treatmentPlas ?? []).length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Protocol Form
                        </Typography>
                        <TreatmentPlanView
                          plans={lastSummary?.treatmentPlas ?? []}
                        />
                      </>
                    ) : null}
                    {isEmrEnabled("ioMonitoring") &&
                    getIoMonitoringList(lastSummary?.ioMonitoring).length >
                      0 ? (
                      <div className="workbook-print-section">
                        <IOMonitoringSummaryView
                          ioMonitoring={lastSummary?.ioMonitoring}
                        />
                      </div>
                    ) : null}
                    {isEmrEnabled("vitals") &&
                    lastSummary?.vitals?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Vitals
                        </Typography>
                        <VitalsView vitals={lastSummary?.vitals} />
                      </>
                    ) : null}
                    {isEmrEnabled("bodyMap") &&
                    lastSummary?.bodymaps?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Body Map
                        </Typography>
                        <BodyMapView bodyMaps={lastSummary?.bodymaps} />
                      </>
                    ) : null}
                    {isEmrEnabled("files") &&
                    lastSummary?.petFiles?.filter((fl) => fl?.appointmentId)
                      ?.length > 0 ? (
                      <>
                        <Typography className="fs14 blue-color font-bold mt10 workbook-print-section">
                          Files
                        </Typography>
                        <FileView
                          files={lastSummary?.petFiles?.filter(
                            (fl) => fl?.appointmentId,
                          )}
                        />
                      </>
                    ) : null}
                    {hasDischargeSummary(lastSummary?.dischargeSummary) ? (
                      <DischargeInstructionsView
                        dischargeSummary={lastSummary?.dischargeSummary}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </ContentToPrint>
      </div>

      <CustomModal
        open={caseStatusMod?.visible}
        onClose={onCloseCaseStatusMod}
        header="Update Status"
        modal
        modalWidth={30}
      >
        <div className="ph20">
          <div className="mt15">
            <Select
              select
              list={workbookStatusList}
              value={caseStatusMod?.caseStatus || ""}
              handleChange={(e) => {
                setCaseStatusMod((prev) => ({
                  ...prev,
                  caseStatus: e?.target?.value ?? "",
                }));
              }}
              label="Condition Status"
            />
          </div>
          <div className="flex1-end mv20">
            <div>
              <CustomButton
                smallBtn
                text="Submit"
                onClick={onUpdateCaseStatus}
                disabled={!caseStatusMod?.caseStatus}
              />
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={editConsultMod?.visible}
        onClose={onCloseEditConsultMod}
        header=""
        modal
        modalWidth={42}
        headerShown={false}
      >
        <Typography className="txt-semi-bold fs16 text-align-center mv20 ph15">
          Are you sure you want to request edit access for this consultation?
        </Typography>
        <div className="ph15 mb10">
          <Select
            list={editConsultSelectList}
            value={editRequestFor}
            handleChange={onEditRequestChange}
            name="editRequestFor"
            label="Select sections to edit"
            multiSelectSearchCheck
            backgroundColor={AppColors.lightBlue2}
            error={!!editRequestErr}
            helperText={editRequestErr}
          />
          {editRequestFor?.some((item) => item?.value === "Others") ? (
            <div className="mt10">
              <CustomTextField
                fullWidth
                label="Others"
                placeholder="Enter remarks"
                value={editRequestOthers}
                handleChange={(e) => {
                  setEditRequestErr("");
                  setEditRequestOthers(e?.target?.value ?? "");
                }}
              />
            </div>
          ) : null}
        </div>
        <div className="flex-center mb20">
          <div className="flex-row-ali-cen">
            <div>
              <CustomButton
                text="Yes"
                redBtn
                smallBtn
                onClick={onConfirmEditConsult}
              />
            </div>
            <div className="ml15">
              <CustomButton
                text="No"
                smallBtn
                onClick={onCloseEditConsultMod}
              />
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={memoDelMod?.visible}
        onClose={onCloseMemoDelMod}
        header="Delete Memo/Records"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete the Memo/Records?
        </Typography>
        <div className="clinic-mod-btn-pos mb10 mt15">
          <div className="mr10">
            <CustomButton
              text="Yes"
              redBtn
              onClick={onConfirmDeleteMemo}
              smallBtn
            />
          </div>
          <div className="ml10">
            <CustomButton text="No" onClick={onCloseMemoDelMod} smallBtn />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={memoEditMod?.visible}
        onClose={onCloseMemoEditMod}
        header="Memo/Records"
        modal
        modalWidth={90}
      >
        <div className="mt10 mh20 mb10">
          <CustomTextField
            label="Memo"
            name="memo"
            fullWidth
            multiline
            labelTop
            value={memoEditText}
            handleChange={(e) => {
              setMemoEditText(e?.target?.value);
              setMemoEditError("");
            }}
            placeholder="Enter memo"
          />
          <div className="mt15">
            {memoEditFiles?.length > 0 ? (
              <FilesWithCloseButton
                files={memoEditFiles}
                onClickFile={(i) => handleRemoveMemoEditFile(i)}
              />
            ) : null}
            <CustomUpload
              onUploadFile={onUploadMemoEditFile}
              multipleNew
              multiFiles
            />
          </div>
          {memoEditError ? (
            <div className="flex-center">
              <Typography color="error" className="fs12 mt5">
                {memoEditError}
              </Typography>
            </div>
          ) : null}
          <div className="flex1-center mb10 mt15">
            <div>
              <CustomButton
                text="Submit"
                smallBtn
                onClick={handleMemoEditSubmit}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default MedicalHistoryNew;
